

































import { computed, defineComponent, ref, toRefs, useRoute, watch } from '@nuxtjs/composition-api'

import useProxyValue from '@/composables/use-proxy-value'
import useCheckUser from '@/composables/use-check-user'
import useConfirmForm from '@/composables/use-confirm-form'

import LoginStep from '@/enums/LoginStep'
import ThirdParty from '@/enums/ThirdParty'

import { appleOauthUri, facebookOauthUri, googleOauthUri } from '@/modules/get-oauth-uri'
import useI18n from '@/composables/use-i18n'
import UAParser from 'ua-parser-js'

type SSOExceptionCode = 'UNSUPPORTED_IN_APP_BROWSER';

class SSOException extends Error {
  readonly code: SSOExceptionCode;
  readonly reason: string;
  readonly provider: ThirdParty;

  constructor (code: SSOExceptionCode, reason: string, provider: ThirdParty) {
    super(`SSO Error [${code}]: ${reason}`)
    this.name = 'SSOException'
    this.code = code
    this.reason = reason
    this.provider = provider
    Error.captureStackTrace(this, this.constructor)
  }
}

type SSORuleValidateRequest = {
  uaParser: UAParser.UAParserInstance;
};

abstract class SSORule {
  readonly provider: ThirdParty;

  constructor (provider: ThirdParty) {
    this.provider = provider
  }

  abstract check(request: SSORuleValidateRequest): boolean;
  abstract getError(): SSOException;
}

class LineInAppBrowserRule extends SSORule {
  check (request: SSORuleValidateRequest): boolean {
    return request.uaParser.getBrowser().name === 'Line'
  }

  getError (): SSOException {
    return new SSOException(
      'UNSUPPORTED_IN_APP_BROWSER',
      'Line in-app browser is not supported for SSO',
      this.provider,
    )
  }
}

class SSORuleEngine {
  private rules: SSORule[] = [];

  addRule (rule: SSORule): this {
    this.rules.push(rule)
    return this
  }

  validate (request: SSORuleValidateRequest): void {
    for (const rule of this.rules) {
      if (rule.check(request)) {
        throw rule.getError()
      }
    }
  }
}

abstract class SSOStrategy {
  readonly uaParser: UAParser.UAParserInstance;
  readonly locale: string;
  protected ruleEngine: SSORuleEngine;

  constructor (uaParser: UAParser.UAParserInstance, locale: string) {
    this.uaParser = uaParser
    this.locale = locale
    this.ruleEngine = new SSORuleEngine()
    this.setupRules()
  }

  /**
   * @description 設定此 provider 的禁用規則
   */
  protected abstract setupRules(): void;

  /**
   * @description 檢查是否可用 SSO 登入
   */
  validateSSO (): void {
    this.ruleEngine.validate({ uaParser: this.uaParser })
  }

  /**
   * 取得 SSO 的 redirect url
   */
  abstract getRedirectUrl(): string;
}

class GoogleSSOStrategy extends SSOStrategy {
  protected setupRules (): void {
    this.ruleEngine.addRule(new LineInAppBrowserRule(ThirdParty.GOOGLE))
  }

  getRedirectUrl (): string {
    return googleOauthUri({ locale: this.locale })
  }
}

class FacebookSSOStrategy extends SSOStrategy {
  protected setupRules (): void {
    // Facebook 目前沒有特殊限制，但保留擴充彈性
  }

  getRedirectUrl (): string {
    return facebookOauthUri({ locale: this.locale })
  }
}

class AppleSSOStrategy extends SSOStrategy {
  protected setupRules (): void {
    // Apple 目前沒有特殊限制，但保留擴充彈性
  }

  getRedirectUrl (): string {
    return appleOauthUri({ locale: this.locale })
  }
}

export default defineComponent({
  props: {
    value: {
      type: String || null,
      default: null,
    },
  },
  setup (props, { emit }) {
    const i18n = useI18n()
    const { proxyValue } = useProxyValue(toRefs(props).value, emit)

    const {
      fields,
    } = useConfirmForm()
    fields.email.value = proxyValue.value

    const route = useRoute()

    const { checkEmailExist } = useCheckUser()

    const step = ref<LoginStep>(LoginStep.LOGIN_OR_SIGNUP)

    // 第一關：檢查 email 是否存在
    const checkEmail = async () => {
      localStorage.setItem('temp-link', route.value.fullPath)

      if (!fields.email.validate()) return
      const exist = await checkEmailExist(fields.email.value)
      if (exist) {
        emit('next-step', LoginStep.LOGIN)
      } else {
        emit('next-step', LoginStep.SIGNUP)
      }
    }

    const ssoException = ref<SSOException | null>(null)

    const strategyMap = computed(() => {
      const parser = new UAParser()
      return {
        [ThirdParty.GOOGLE]: new GoogleSSOStrategy(parser, i18n.locale),
        [ThirdParty.APPLE]: new AppleSSOStrategy(parser, i18n.locale),
        [ThirdParty.FACEBOOK]: new FacebookSSOStrategy(parser, i18n.locale),
      }
    })

    const loginThirdParty = (source: ThirdParty) => {
      try {
        const strategy = strategyMap.value[source]
        strategy.validateSSO()

        localStorage.setItem('temp-link', route.value.fullPath)
        pressLogin.value = true
        const redirectUrl = strategy.getRedirectUrl()

        location.href = redirectUrl
        pressLogin.value = false
      } catch (error) {
        if (error instanceof SSOException) {
          ssoException.value = error
        }
      }
    }

    const pressLogin = ref(false)

    watch(fields.email, (v) => {
      proxyValue.value = v.value
    })

    return {
      ThirdParty,
      loginThirdParty,

      LoginStep,

      step,
      fields,
      checkEmail,

      pressLogin,

      strategyMap,
      ssoException,
    }
  },
})
